import type { MutationFunction } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import type { Nullable } from '@asseco/react-native-gov-components';
import {
	delay,
	getAntiVirusWarningHeader,
	getDispositionResponseHeader,
	getFileNameFromDispositionHeader,
} from '@gov-nx/utils/common';
import type { PoQueryConfig } from './types';
import { usePoMutation } from './usePoMutation';

export interface DocumentDownload<Variables> {
	variables: Variables;
	blob: Blob;
	fileName: string;
	antivirus: Nullable<string>;
}

export interface DocumentDownloadProps<Variables> {
	onError?: (error: Error, variables: Variables) => void;
	onSuccess?: (data: DocumentDownload<Variables>) => void;
	config?: PoQueryConfig;
}

export const useDownloadPoMutation = <Variables>(
	mutationFn: MutationFunction<AxiosResponse, Variables>,
	props: DocumentDownloadProps<Variables>
) => {
	const mutation = usePoMutation(
		{
			mutationFn,
			onError: (error, variables) => {
				props?.onError && props.onError(error, variables);
			},
			onSuccess: async (response, variables) => {
				if (response.status === 202) {
					await delay(2000);
					mutation.mutate(variables);
				} else {
					try {
						const disposition = getDispositionResponseHeader(response);
						const antivirus = getAntiVirusWarningHeader(response);
						const fileName = getFileNameFromDispositionHeader(disposition);
						const blob = response.data;
						props?.onSuccess &&
							props.onSuccess({ antivirus, fileName, blob, variables });
					} catch (error) {
						props?.onError && props.onError(error as Error, variables);
					}
				}
			},
		},
		props.config
	);

	return mutation;
};
